import { baseApi } from '@/api/baseApi';
import type {
    ApproveCafeApplicationRequest,
    ApproveCafeApplicationResponse,
    GetCafeApplicationResponse,
    ListCafeApplicationsFilters,
    ListCafeApplicationsResponse,
    RejectCafeApplicationRequest,
    RejectCafeApplicationResponse
} from '@/lib/cafeApplicationsSchemas';
import {
    ApproveCafeApplicationResponseSchema,
    GetCafeApplicationResponseSchema,
    ListCafeApplicationsResponseSchema,
    RejectCafeApplicationResponseSchema
} from '@/lib/cafeApplicationsSchemas';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /cafe-applications/*
 * (фича cafe-company-verification, MF-5). admin-backend на порту 5002
 * без префикса /admin.
 *
 * «Заявка кафе» = `Cafe` в статусе модерации (отдельной entity нет —
 * модель на `Cafe` + `CafeSettings`); `:id` = `cafeId`.
 */
export const cafeApplicationsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listCafeApplications: build.query<
            ListCafeApplicationsResponse,
            ListCafeApplicationsFilters | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.status) {
                    search.status = params.status;
                }
                if (params?.search !== undefined && params.search !== '') {
                    search.search = params.search;
                }
                if (params?.limit !== undefined) {
                    search.limit = String(params.limit);
                }
                if (params?.offset !== undefined) {
                    search.offset = String(params.offset);
                }
                return {
                    url: '/cafe-applications',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): ListCafeApplicationsResponse =>
                parseWithSchema(ListCafeApplicationsResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((a) => ({
                            type: 'CafeApplication' as const,
                            id: a.cafeId
                        })),
                        { type: 'CafeApplication' as const, id: 'LIST' }
                    ]
                    : [{ type: 'CafeApplication' as const, id: 'LIST' }]
        }),

        getCafeApplication: build.query<GetCafeApplicationResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/cafe-applications/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): GetCafeApplicationResponse =>
                parseWithSchema(GetCafeApplicationResponseSchema, raw),
            providesTags: (_result, _error, arg) => [
                { type: 'CafeApplication' as const, id: arg.id }
            ]
        }),

        approveCafeApplication: build.mutation<
            ApproveCafeApplicationResponse,
            { id: string; body: ApproveCafeApplicationRequest }
        >({
            query: ({ id, body }) => ({
                url: `/cafe-applications/${id}/approve`,
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): ApproveCafeApplicationResponse =>
                parseWithSchema(ApproveCafeApplicationResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'CafeApplication', id: arg.id },
                { type: 'CafeApplication', id: 'LIST' }
            ]
        }),

        rejectCafeApplication: build.mutation<
            RejectCafeApplicationResponse,
            { id: string; body: RejectCafeApplicationRequest }
        >({
            query: ({ id, body }) => ({
                url: `/cafe-applications/${id}/reject`,
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): RejectCafeApplicationResponse =>
                parseWithSchema(RejectCafeApplicationResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'CafeApplication', id: arg.id },
                { type: 'CafeApplication', id: 'LIST' }
            ]
        })
    })
});

export const {
    useListCafeApplicationsQuery,
    useGetCafeApplicationQuery,
    useApproveCafeApplicationMutation,
    useRejectCafeApplicationMutation
} = cafeApplicationsApi;
